import React, { type ReactNode } from 'react';
import CodeIcon from '@atlaskit/icon/glyph/code';
import FileIcon from '@atlaskit/icon/glyph/file';
import SuitcaseIcon from '@atlaskit/icon/glyph/suitcase';
import { JiraIcon, TrelloIcon } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { fg } from '@atlassian/jira-feature-gating';
import AirtableLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/airtable-logomark';
import AsanaLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/asana-logomark';
import AzureLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/azure-logomark';
import ClickUpLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/clickup-logomark';
import GitHubLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/github-invertocat-logomark';
import GitlabLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/gitlab-logomark';
import GoogleSheetLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/google-sheet-logomark';
import JetbrainsYouTrackLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/jetbrains-youtrack-logomark';
import LinearLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/linear-logomark';
import MSExcelLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/microsoft-excel-logomark';
import MondayLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/monday-logomark';
import NotionLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/notion-logomark';
import SmartSheetLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/smartsheet-logomark';
import WrikeLogomark from '@atlassian/jira-illustrations/src/ui/adg4/thirdparty/logos/components/wrike-logomark';
import type { MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import AirtableIcon from '../../assets/Airtable.svg';
import AsanaIcon from '../../assets/Asana.svg';
import AzureIcon from '../../assets/Azure.svg';
import ClickUpIcon from '../../assets/ClickUp.svg';
import GitHubIcon from '../../assets/git-hub';
import GitLabIcon from '../../assets/GitLab.svg';
import GoogleSheetIcon from '../../assets/GoogleSheet.svg';
import JetbrainsYouTrackIcon from '../../assets/JetbrainsYouTrack.svg';
import LinearIcon from '../../assets/linear';
import MondayIcon from '../../assets/Monday.svg';
import MSExcelIcon from '../../assets/MSExcel.svg';
import NotionIcon from '../../assets/notion';
import SmartSheetIcon from '../../assets/smart-sheet';
import WrikeIcon from '../../assets/Wrike.svg';
import { IconType, type Icon } from '../../ui/icon';
import {
	TRELLO,
	SHEETS,
	EXCEL,
	MONDAY,
	ASANA,
	CLICK_UP,
	AIRTABLE,
	SMART_SHEET,
	GITHUB,
	LINEAR,
	GITLAB,
	MONDAY_DISPLAY_NAME,
	TRELLO_DISPLAY_NAME,
	GOOGLE_SHEETS_DISPLAY_NAME,
	EXCEL_DISPLAY_NAME,
	ASANA_DISPLAY_NAME,
	CLICK_UP_DISPLAY_NAME,
	AIRTABLE_DISPLAY_NAME,
	SMART_SHEET_DISPLAY_NAME,
	GITHUB_DISPLAY_NAME,
	LINEAR_DISPLAY_NAME,
	CSV,
	JIRA_SOFTWARE,
	GITLAB_DISPLAY_NAME,
	JIRA_BUSINESS,
	AZURE_DEVOPS_DISPLAY_NAME,
	AZURE_DEVOPS,
	NOTION,
	NOTION_DISPLAY_NAME,
	WRIKE,
	WRIKE_DISPLAY_NAME,
	JETBRAINS_YOUTRACK,
	JETBRAINS_YOUTRACK_DISPLAY_NAME,
} from '../import-type';
import { ProjectTypes } from '../project';
import messages from './messages';

type AuthConfig = {
	requiresOAuth?: boolean;
	requiresPersonalAccessToken?: boolean;
};

export const SourceType = {
	THIRD_PARTY: 'thirdParty',
	CSV: 'CSV',
	JIRA: 'Jira',
} as const;

export type SourceType = (typeof SourceType)[keyof typeof SourceType];

export type SourceItem = {
	key: string;
	name: string;
	icon: Icon;
	label?: string;
	sourceType?: SourceType;
} & AuthConfig;

export const getSourceItemMapOld: (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => Record<string, SourceItem> = (formatMessage) => ({
	[CSV]: {
		key: CSV,
		name: CSV,
		icon: {
			type: IconType.LOGO,
			component: () => <FileIcon label="" />,
			props: {
				size: 'medium',
				appearance: 'brand',
			},
		},
		sourceType: SourceType.CSV,
	},
	[ASANA]: {
		key: ASANA,
		name: ASANA_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: AsanaIcon, alt: 'Asana Icon' },
		},
		requiresOAuth: true,
		sourceType: SourceType.THIRD_PARTY,
	},
	[JIRA_SOFTWARE]: {
		key: JIRA_SOFTWARE,
		name: formatMessage(messages.jiraSoftwareName),
		icon: {
			type: IconType.LOGO,
			component: JiraIcon,
			props: {
				appearance: 'brand',
			},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[JIRA_BUSINESS]: {
		key: JIRA_BUSINESS,
		name: formatMessage(messages.jiraBusinessName),
		icon: {
			type: IconType.LOGO,
			component: JiraIcon,
			props: {
				appearance: 'brand',
			},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[TRELLO]: {
		key: TRELLO,
		name: TRELLO_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: TrelloIcon,
			props: { appearance: 'brand' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[MONDAY]: {
		key: MONDAY,
		name: MONDAY_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: MondayIcon, alt: 'Monday Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
		requiresPersonalAccessToken: true,
	},
	[CLICK_UP]: {
		key: CLICK_UP,
		name: CLICK_UP_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: ClickUpIcon, alt: 'ClickUp Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
		requiresOAuth: true,
	},
	[SHEETS]: {
		key: SHEETS,
		name: GOOGLE_SHEETS_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: GoogleSheetIcon, alt: 'GoogleSheet Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[EXCEL]: {
		key: EXCEL,
		name: EXCEL_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: MSExcelIcon, alt: 'MSExcel Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[AIRTABLE]: {
		key: AIRTABLE,
		name: AIRTABLE_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: AirtableIcon, alt: 'Airtable Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[SMART_SHEET]: {
		key: SMART_SHEET,
		name: SMART_SHEET_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: SmartSheetIcon,
			props: {},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[GITHUB]: {
		key: GITHUB,
		name: GITHUB_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: GitHubIcon,
			props: {},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[LINEAR]: {
		key: LINEAR,
		name: LINEAR_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: LinearIcon,
			props: {},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[GITLAB]: {
		key: GITLAB,
		name: GITLAB_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: GitLabIcon, alt: 'GitLab Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[AZURE_DEVOPS]: {
		key: AZURE_DEVOPS,
		name: AZURE_DEVOPS_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: AzureIcon, alt: 'Azure DevOps Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[NOTION]: {
		key: NOTION,
		name: NOTION_DISPLAY_NAME,
		icon: {
			type: IconType.LOGO,
			component: NotionIcon,
			props: {},
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[WRIKE]: {
		key: WRIKE,
		name: WRIKE_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: WrikeIcon, alt: 'Wrike Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
	[JETBRAINS_YOUTRACK]: {
		key: JETBRAINS_YOUTRACK,
		name: JETBRAINS_YOUTRACK_DISPLAY_NAME,
		icon: {
			type: IconType.IMAGE,
			props: { src: JetbrainsYouTrackIcon, alt: 'Jetbrains Icon' },
		},
		sourceType: SourceType.THIRD_PARTY,
	},
});

export const getSourceItemMap: (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => Record<string, SourceItem> = (formatMessage) => {
	if (!fg('x2c-consolidate-assets')) {
		return getSourceItemMapOld(formatMessage);
	}

	return {
		[CSV]: {
			key: CSV,
			name: CSV,
			icon: {
				type: IconType.LOGO,
				component: () => <FileIcon label="" />,
				props: {
					size: 'medium',
					appearance: 'brand',
				},
			},
			sourceType: SourceType.CSV,
		},
		[ASANA]: {
			key: ASANA,
			name: ASANA_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: AsanaLogomark,
				props: { alt: 'Asana Icon' },
			},
			requiresOAuth: true,
			sourceType: SourceType.THIRD_PARTY,
		},
		[JIRA_SOFTWARE]: {
			key: JIRA_SOFTWARE,
			name: formatMessage(messages.jiraSoftwareName),
			icon: {
				type: IconType.LOGO,
				component: JiraIcon,
				props: {
					appearance: 'brand',
				},
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[JIRA_BUSINESS]: {
			key: JIRA_BUSINESS,
			name: formatMessage(messages.jiraBusinessName),
			icon: {
				type: IconType.LOGO,
				component: JiraIcon,
				props: {
					appearance: 'brand',
				},
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[TRELLO]: {
			key: TRELLO,
			name: TRELLO_DISPLAY_NAME,
			icon: {
				type: IconType.LOGO,
				component: TrelloIcon,
				props: { appearance: 'brand' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[MONDAY]: {
			key: MONDAY,
			name: MONDAY_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: MondayLogomark,
				props: { alt: 'monday Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
			requiresPersonalAccessToken: true,
		},
		[CLICK_UP]: {
			key: CLICK_UP,
			name: CLICK_UP_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: ClickUpLogomark,
				props: { alt: 'ClickUp Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
			requiresOAuth: true,
		},
		[SHEETS]: {
			key: SHEETS,
			name: GOOGLE_SHEETS_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: GoogleSheetLogomark,
				props: { alt: 'GoogleSheet Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[EXCEL]: {
			key: EXCEL,
			name: EXCEL_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: MSExcelLogomark,
				props: { alt: 'MSExcel Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[AIRTABLE]: {
			key: AIRTABLE,
			name: AIRTABLE_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: AirtableLogomark,
				props: { alt: 'Airtable Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[SMART_SHEET]: {
			key: SMART_SHEET,
			name: SMART_SHEET_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: SmartSheetLogomark,
				props: { alt: 'SmartSheet Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[GITHUB]: {
			key: GITHUB,
			name: GITHUB_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: GitHubLogomark,
				props: { alt: 'GitHub Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[LINEAR]: {
			key: LINEAR,
			name: LINEAR_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: LinearLogomark,
				props: { alt: 'Linear Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[GITLAB]: {
			key: GITLAB,
			name: GITLAB_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: GitlabLogomark,
				props: { alt: 'GitLab Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[AZURE_DEVOPS]: {
			key: AZURE_DEVOPS,
			name: AZURE_DEVOPS_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: AzureLogomark,
				props: { alt: 'Azure DevOps Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[NOTION]: {
			key: NOTION,
			name: NOTION_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: NotionLogomark,
				props: { alt: 'Notion Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[WRIKE]: {
			key: WRIKE,
			name: WRIKE_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: WrikeLogomark,
				props: { alt: 'Wrike Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
		[JETBRAINS_YOUTRACK]: {
			key: JETBRAINS_YOUTRACK,
			name: JETBRAINS_YOUTRACK_DISPLAY_NAME,
			icon: {
				type: IconType.LOGOMARK,
				component: JetbrainsYouTrackLogomark,
				props: { alt: 'Jetbrains Icon' },
			},
			sourceType: SourceType.THIRD_PARTY,
		},
	};
};

export const getJiraProjects = (): Record<
	ProjectType,
	| {
			name: MessageDescriptor;
			icon: ReactNode;
			description?: MessageDescriptor;
	  }
	| undefined
> => ({
	[ProjectTypes.SOFTWARE]: {
		name: messages.jiraSoftware,
		icon: (
			<CodeIcon
				label="Software project"
				primaryColor={token('color.text.accent.blue', '#0052CC')}
				size="medium"
			/>
		),
		description: messages.jiraSoftwareDescription,
	},
	[ProjectTypes.BUSINESS]: {
		name: messages.jiraBusiness,
		icon: (
			<SuitcaseIcon
				label="Business project"
				primaryColor={token('color.text.accent.blue', '#0052CC')}
				size="medium"
			/>
		),
		description: messages.jiraBusinessDescription,
	},
	customer_service: undefined,
	service_desk: undefined,
	product_discovery: undefined,
});
